import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Play, Plus, Flame, MapPin, Trophy, ChefHat, 
  ArrowRight, ArrowLeft, X, Sparkles, Heart,
  Users, Star, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const ONBOARDING_KEY = 'mharher_onboarding_completed';

// Get onboarding steps with translations
const getOnboardingSteps = (t) => [
  {
    id: 'welcome',
    title: t('onboarding.welcome.title'),
    subtitle: t('onboarding.welcome.subtitle'),
    description: t('onboarding.welcome.description'),
    icon: ChefHat,
    gradient: 'from-[#1E4ED8] to-[#3B82F6]',
    illustration: '🌶️',
    features: [
      { icon: Users, text: t('onboarding.welcome.activeChefs') },
      { icon: ChefHat, text: t('onboarding.welcome.authenticRecipes') },
      { icon: MapPin, text: t('onboarding.welcome.regionsToDiscover') },
    ]
  },
  {
    id: 'video-feed',
    title: t('onboarding.videoFeed.title'),
    subtitle: t('onboarding.videoFeed.subtitle'),
    description: t('onboarding.videoFeed.description'),
    icon: Play,
    gradient: 'from-[#D9381E] to-[#E07A5F]',
    illustration: '📱',
    features: [
      { icon: Play, text: t('onboarding.videoFeed.immersiveVideos') },
      { icon: Heart, text: t('onboarding.videoFeed.saveFavorites') },
      { icon: Users, text: t('onboarding.videoFeed.followChefs') },
    ]
  },
  {
    id: 'create',
    title: t('onboarding.create.title'),
    subtitle: t('onboarding.create.subtitle'),
    description: t('onboarding.create.description'),
    icon: Plus,
    gradient: 'from-[#4F6F52] to-[#6B8E6B]',
    illustration: '✨',
    features: [
      { icon: Play, text: t('onboarding.create.culinaryVideos') },
      { icon: ChefHat, text: t('onboarding.create.fullRecipes') },
      { icon: Sparkles, text: t('onboarding.create.ephemeralStories') },
    ]
  },
  {
    id: 'barka',
    title: t('onboarding.barka.title'),
    subtitle: t('onboarding.barka.subtitle'),
    description: t('onboarding.barka.description'),
    icon: Flame,
    gradient: 'from-[#D9381E] to-[#F4A261]',
    illustration: '🔥',
    features: [
      { icon: Flame, text: t('onboarding.barka.earnBarka') },
      { icon: Trophy, text: t('onboarding.barka.levelUp') },
      { icon: Star, text: t('onboarding.barka.unlockBadges') },
    ]
  },
  {
    id: 'explore',
    title: t('onboarding.explore.title'),
    subtitle: t('onboarding.explore.subtitle'),
    description: t('onboarding.explore.description'),
    icon: MapPin,
    gradient: 'from-[#1E4ED8] to-[#60A5FA]',
    illustration: '🇹🇳',
    features: [
      { icon: MapPin, text: t('onboarding.explore.interactiveMap') },
      { icon: Trophy, text: t('onboarding.explore.regionalChallenges') },
      { icon: ChefHat, text: t('onboarding.explore.localTopChefs') },
    ]
  },
];

// Individual step component
const OnboardingStep = ({ step, isActive, direction }) => {
  const Icon = step.icon;
  
  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        x: direction === 'next' ? 100 : -100,
        scale: 0.95
      }}
      animate={{ 
        opacity: isActive ? 1 : 0, 
        x: isActive ? 0 : (direction === 'next' ? -100 : 100),
        scale: isActive ? 1 : 0.95
      }}
      exit={{ 
        opacity: 0, 
        x: direction === 'next' ? -100 : 100,
        scale: 0.95
      }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
    >
      {/* Illustration Circle */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className={`w-32 h-32 md:w-40 md:h-40 rounded-3xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-2xl mb-8`}
      >
        <span className="text-6xl md:text-7xl">{step.illustration}</span>
      </motion.div>

      {/* Icon Badge */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`w-12 h-12 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg -mt-14 mb-6 border-4 border-white`}
      >
        <Icon className="w-6 h-6 text-white" />
      </motion.div>

      {/* Text Content */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="max-w-md"
      >
        <h2 className="font-fraunces text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          {step.title}
        </h2>
        <p className="text-[#1E4ED8] font-semibold mb-4">{step.subtitle}</p>
        <p className="text-gray-600 leading-relaxed mb-8">
          {step.description}
        </p>
      </motion.div>

      {/* Features */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex flex-wrap justify-center gap-3"
      >
        {step.features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6 + index * 0.1 }}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100"
          >
            <feature.icon className="w-4 h-4 text-[#1E4ED8]" />
            <span className="text-sm font-medium text-gray-700">{feature.text}</span>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

// Progress indicator
const ProgressIndicator = ({ currentStep, totalSteps, onStepClick }) => {
  return (
    <div className="flex items-center gap-2">
      {[...Array(totalSteps)].map((_, index) => (
        <button
          key={index}
          onClick={() => onStepClick(index)}
          className={`transition-all duration-300 rounded-full ${
            index === currentStep
              ? 'w-8 h-2 bg-[#1E4ED8]'
              : index < currentStep
              ? 'w-2 h-2 bg-[#1E4ED8]/50'
              : 'w-2 h-2 bg-gray-300'
          }`}
          aria-label={`Go to step ${index + 1}`}
        />
      ))}
    </div>
  );
};

// Main Onboarding Component
export function Onboarding({ onComplete }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('next');
  const navigate = useNavigate();
  const { user } = useAuth();

  const onboardingSteps = getOnboardingSteps(t);
  const isLastStep = currentStep === onboardingSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      completeOnboarding();
    } else {
      setDirection('next');
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setDirection('prev');
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const handleStepClick = (stepIndex) => {
    setDirection(stepIndex > currentStep ? 'next' : 'prev');
    setCurrentStep(stepIndex);
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    if (user?.id) {
      localStorage.setItem(`${ONBOARDING_KEY}_${user.id}`, 'true');
    }
    onComplete?.();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#F8F5F2] flex flex-col"
      data-testid="onboarding-modal"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#1E4ED8]/5" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#D9381E]/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-[#1E4ED8]/5 to-transparent" />
      </div>

      {/* Header */}
      <div className="relative flex items-center justify-between p-4 md:p-6">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-lg">
            <span className="text-xl">🌶️</span>
          </div>
          <span className="font-fraunces font-bold text-xl text-gray-900 hidden md:block">MHARHER</span>
        </div>
        
        <button
          onClick={handleSkip}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors px-4 py-2 rounded-full hover:bg-gray-100"
          data-testid="skip-onboarding-btn"
        >
          <span className="text-sm font-medium">{t('common.skip')}</span>
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <OnboardingStep
            key={currentStep}
            step={onboardingSteps[currentStep]}
            isActive={true}
            direction={direction}
          />
        </AnimatePresence>
      </div>

      {/* Footer Navigation */}
      <div className="relative p-4 md:p-6">
        <div className="max-w-lg mx-auto">
          {/* Progress */}
          <div className="flex justify-center mb-6">
            <ProgressIndicator
              currentStep={currentStep}
              totalSteps={onboardingSteps.length}
              onStepClick={handleStepClick}
            />
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`flex items-center gap-2 rounded-full px-6 transition-all ${
                isFirstStep ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              data-testid="onboarding-prev-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('common.previous')}</span>
            </Button>

            <Button
              onClick={handleNext}
              className={`flex items-center gap-2 rounded-full px-8 py-6 text-lg font-semibold shadow-xl transition-all ${
                isLastStep
                  ? 'bg-gradient-to-r from-[#D9381E] to-[#E07A5F] hover:from-[#B82E19] hover:to-[#D9381E] shadow-[#D9381E]/30'
                  : 'bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] hover:from-[#1E40AF] hover:to-[#2563EB] shadow-[#1E4ED8]/30'
              }`}
              data-testid="onboarding-next-btn"
            >
              {isLastStep ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>{t('common.start')}</span>
                </>
              ) : (
                <>
                  <span>{t('common.next')}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>

          {/* Step counter */}
          <p className="text-center text-sm text-gray-400 mt-4">
            {currentStep + 1} {t('common.of')} {onboardingSteps.length}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Hook to check if onboarding should be shown
export function useOnboarding() {
  // This is now a simple re-export from the context
  // Import useOnboardingContext from context/OnboardingContext instead
  const { user, isAuthenticated } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user has completed onboarding
      const globalCompleted = localStorage.getItem(ONBOARDING_KEY);
      const userCompleted = localStorage.getItem(`${ONBOARDING_KEY}_${user.id}`);
      
      // Show onboarding if not completed
      if (!globalCompleted && !userCompleted) {
        setShowOnboarding(true);
      }
      setIsChecked(true);
    }
  }, [isAuthenticated, user]);

  const completeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_KEY, 'true');
    if (user?.id) {
      localStorage.setItem(`${ONBOARDING_KEY}_${user.id}`, 'true');
    }
  };

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
    if (user?.id) {
      localStorage.removeItem(`${ONBOARDING_KEY}_${user.id}`);
    }
    setShowOnboarding(true);
  };

  return {
    showOnboarding,
    isChecked,
    completeOnboarding,
    resetOnboarding
  };
}

// Export for settings page to allow replay
export function OnboardingReplayButton() {
  const { t } = useTranslation();
  const { resetOnboarding } = useOnboarding();
  
  return (
    <Button
      variant="outline"
      onClick={resetOnboarding}
      className="border-[#1E4ED8] text-[#1E4ED8] hover:bg-[#1E4ED8]/10 rounded-full"
      data-testid="replay-onboarding-btn"
    >
      <Play className="w-4 h-4 mr-2" />
      {t('settings.replayTutorial')}
    </Button>
  );
}

export default Onboarding;
